import { useEffect, useState } from 'react';
import { Card, Loading } from '../common';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { readingsAPI, type ReadingStatusResponse } from '../../api/readings';

interface ReadingGenerationStatusProps {
  readingId: string;
  itemDeducted: boolean;
  onComplete: (readingId: string) => void;
  onError: (error: string) => void;
}

export function ReadingGenerationStatus({
  readingId,
  itemDeducted,
  onComplete,
  onError
}: ReadingGenerationStatusProps) {
  const [status, setStatus] = useState<ReadingStatusResponse | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let intervalId: NodeJS.Timeout;
    let isMounted = true;
    let currentPollCount = 0;
    let startTime = Date.now();

    // Track elapsed time
    intervalId = setInterval(() => {
      if (isMounted) {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }
    }, 1000);

    const pollStatus = async () => {
      try {
        // Use the API client which handles authentication automatically
        const response = await readingsAPI.getReadingStatus(readingId);

        if (!isMounted) return;

        setStatus(response.data);
        currentPollCount++;
        setPollCount(currentPollCount);

        // Check if generation is complete
        if (response.data.status === 'completed') {
          onComplete(readingId);
          return; // Stop polling
        }

        // Check if generation failed
        if (response.data.status === 'failed') {
          const errorMsg = response.data.metadata?.error || 'AI generation failed';
          setError(errorMsg);
          onError(errorMsg);
          return; // Stop polling
        }

        // Calculate elapsed time
        const elapsed = (Date.now() - startTime) / 1000;

        // Timeout after 90 seconds (OpenAI should respond within 60s)
        if (elapsed > 90) {
          const timeoutMsg = 'AI generation is taking longer than expected. Your reading has been saved and will appear in your history once complete.';
          setError(timeoutMsg);
          onError(timeoutMsg);
          return; // Stop polling
        }

        // Improved polling strategy:
        // - First 30s: Poll every 5s (6 requests)
        // - 30-60s: Poll every 10s (3 requests)
        // - 60-90s: Poll every 15s (2 requests)
        // Total: ~11 requests over 90 seconds
        let delay: number;
        if (elapsed < 30) {
          delay = 5000; // 5 seconds for first 30s
        } else if (elapsed < 60) {
          delay = 10000; // 10 seconds for 30-60s
        } else {
          delay = 15000; // 15 seconds for 60-90s
        }
        
        timeoutId = setTimeout(pollStatus, delay);

      } catch (err) {
        if (!isMounted) return;
        
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        
        // Check if it's a rate limit error
        if (errorMsg.includes('429') || errorMsg.includes('rate limit')) {
          setError('Too many requests. Please wait a moment and check your reading history.');
        } else {
          setError(errorMsg);
        }
        
        onError(errorMsg);
        // Stop polling on error
      }
    };

    // Start polling after a 3-second delay to give OpenAI time to start
    timeoutId = setTimeout(pollStatus, 3000);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [readingId, onComplete, onError]); // Removed pollCount from dependencies

  return (
    <Card className="max-w-2xl mx-auto">
      <div className="text-center space-y-6">
        {/* Status Icon */}
        <div className="flex justify-center">
          {error ? (
            <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
          ) : status?.status === 'completed' ? (
            <div className="w-20 h-20 bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          ) : (
            <div className="w-20 h-20 bg-primary-900/20 rounded-full flex items-center justify-center">
              <Loader2 className="h-12 w-12 text-primary-500 animate-spin" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <div>
          {itemDeducted && !error && (
            <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <div className="flex items-center justify-center space-x-2 text-green-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Reading item deducted successfully</span>
              </div>
            </div>
          )}

          {error ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">
                Generation Failed
              </h2>
              <p className="text-gray-400 mb-4">
                {error}
              </p>
              <p className="text-sm text-gray-500">
                Your reading has been saved and can be found in your history.
                The item has been deducted but you can try generating the interpretation again.
              </p>
            </>
          ) : status?.status === 'completed' ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">
                Reading Complete!
              </h2>
              <p className="text-gray-400">
                Your AI interpretation is ready
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">
                Generating Your Reading...
              </h2>
              <p className="text-gray-400 mb-4">
                Our AI is crafting your personalized interpretation
              </p>
              
              {/* Progress Indicator */}
              <div className="space-y-2">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <Loading size="sm" />
                  <span>
                    {elapsedTime < 10 && 'Initializing...'}
                    {elapsedTime >= 10 && elapsedTime < 25 && 'Analyzing cards...'}
                    {elapsedTime >= 25 && elapsedTime < 50 && 'Generating interpretation...'}
                    {elapsedTime >= 50 && 'Finalizing reading...'}
                  </span>
                </div>
                
                {/* Elapsed time and estimated time */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    Elapsed: {elapsedTime}s {elapsedTime < 30 ? '(Usually 10-30 seconds)' : elapsedTime < 60 ? '(Taking a bit longer...)' : '(Almost there...)'}
                  </p>
                  {pollCount > 0 && (
                    <p className="text-xs text-gray-700">
                      Status checks: {pollCount}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Additional Info */}
        {!error && status?.status === 'pending' && (
          <div className="pt-4 border-t border-dark-700">
            <p className="text-sm text-gray-500">
              You can safely leave this page - your reading will be available in your history
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
